import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ButtonBase from '@mui/material/ButtonBase';
import Tooltip from '@mui/material/Tooltip';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { styled } from '@mui/material/styles';
import { Array as A, Option, pipe } from 'effect';

import { countMarkedPlaces, MARKED_ATTRIBUTES } from '@/lib/i18nOverrides/domScan';
import {
  browserMeasure,
  measureSources,
  scanMarkedSources,
  type MeasuredSource,
  type TextSource,
} from '@/lib/i18nOverrides/markedTargets';
import { stripMarkers } from '@/lib/i18nOverrides/markers';
import { DEFAULT_LAYOUT, hoveredTarget, layoutPencils, type PencilPlacement } from '@/lib/i18nOverrides/pencilLayout';
import { markerRegistry } from '@/lib/i18nOverrides/registry';
import { EDITOR_UI_ATTRIBUTE, isInsideEditorUi, STOP_HANDLERS } from '@/components/TextOverrides/editorUi';

const isOwnUi = (el: Element) => isInsideEditorUi(el);

// Mutations that can add, remove or change a marked text; anything else (class, style) only moves things.
const TEXT_ATTRIBUTES: ReadonlyArray<string> = MARKED_ATTRIBUTES;
// Transitions and animations move texts without any event; a slow re-measure catches them.
const REMEASURE_INTERVAL_MS = 400;

const Layer = styled('div')(({ theme }) => ({
  position: 'fixed',
  inset: 0,
  pointerEvents: 'none',
  zIndex: theme.zIndex.textOverridesOverlay,
}));

const Outline = styled('div')(({ theme }) => ({
  position: 'fixed',
  pointerEvents: 'none',
  borderRadius: 2,
  boxShadow: `inset 0 0 0 2px ${theme.palette.warning.main}`,
  background: 'rgba(255, 167, 38, 0.08)',
}));

const Pencil = styled(ButtonBase, {
  shouldForwardProp: (p) => p !== 'collapsed' && p !== 'emphasised',
})<{ collapsed: boolean; emphasised: boolean }>(({ theme, collapsed, emphasised }) => ({
  position: 'fixed',
  width: DEFAULT_LAYOUT.size,
  height: DEFAULT_LAYOUT.size,
  borderRadius: '50%',
  color: theme.palette.common.white,
  background: emphasised ? theme.palette.warning.dark : theme.palette.primary.main,
  border: `1px solid ${theme.palette.common.white}`,
  boxShadow: theme.shadows[2],
  transform: emphasised ? 'scale(1.3)' : 'none',
  transition: 'transform 80ms linear, opacity 80ms linear',
  zIndex: emphasised ? 2 : 1,
  // A collapsed pencil stays in the tab order and shows when focused or when its text is hovered.
  opacity: collapsed && !emphasised ? 0 : 1,
  pointerEvents: collapsed && !emphasised ? 'none' : 'auto',
  '&:focus-visible': { opacity: 1, outline: `2px solid ${theme.palette.warning.main}`, outlineOffset: 1 },
  '& svg': { fontSize: 12 },
}));

const AttributeBadge = styled('span')(({ theme }) => ({
  position: 'absolute',
  right: -6,
  bottom: -6,
  minWidth: 12,
  height: 12,
  padding: '0 2px',
  borderRadius: 6,
  fontSize: 8,
  lineHeight: '12px',
  fontWeight: 700,
  color: theme.palette.common.white,
  background: theme.palette.secondary.main,
}));

const BADGE: Readonly<Record<Exclude<TextSource, 'text'>, string>> = {
  title: 'T',
  placeholder: 'P',
  'aria-label': 'A',
  alt: 'Alt',
};

const SOURCE_NAME: Readonly<Record<TextSource, string>> = {
  text: 'text',
  title: 'tooltip (title)',
  placeholder: 'placeholder',
  'aria-label': 'accessible label',
  alt: 'image text (alt)',
};

const keysOf = (source: MeasuredSource): ReadonlyArray<string> =>
  pipe(
    source.indexes,
    A.filterMap((i) => markerRegistry.get(i)),
    A.map((e) => e.id),
    A.dedupe,
  );

const visibleText = (source: MeasuredSource): string =>
  stripMarkers(
    source.source === 'text'
      ? source.nodes.map((n) => n.nodeValue ?? '').join('')
      : (source.element.getAttribute(source.source) ?? ''),
  ).trim();

const snippet = (s: string) => (s.length > 40 ? `${s.slice(0, 40)}…` : s);

interface PencilButtonProps {
  readonly source: MeasuredSource;
  readonly placement: PencilPlacement;
  readonly emphasised: boolean;
  readonly onOpen: (indexes: ReadonlyArray<number>) => void;
  readonly onFocusChange: (id: string | null) => void;
}

const PencilButton = ({ source, placement, emphasised, onOpen, onFocusChange }: PencilButtonProps) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const keys = keysOf(source);
  const firstKey = keys[0];
  const text = snippet(visibleText(source));
  const what = SOURCE_NAME[source.source];

  // Counting scans the document: only while the tooltip is open.
  const usedIn = useMemo(
    () =>
      tooltipOpen
        ? countMarkedPlaces(document.body, (i) =>
            pipe(
              markerRegistry.get(i),
              Option.exists((e) => e.id === firstKey),
            ),
          )
        : 0,
    [tooltipOpen, firstKey],
  );

  const title =
    keys.length > 1
      ? `Edit ${what} · ${keys.length} texts here: ${keys.join(', ')}`
      : `Edit ${what} · ${keys[0] ?? 'unknown key'} · used in ${usedIn} ${usedIn === 1 ? 'place' : 'places'} on screen`;

  return (
    <Tooltip
      title={title}
      open={tooltipOpen}
      onOpen={() => setTooltipOpen(true)}
      onClose={() => setTooltipOpen(false)}
      placement="top"
      disableInteractive
      slotProps={{ popper: { sx: { zIndex: 'textOverridesEditor' }, [EDITOR_UI_ATTRIBUTE]: '' } as object }}
    >
      <Pencil
        collapsed={placement.collapsed}
        emphasised={emphasised}
        style={{ left: placement.box.left, top: placement.box.top }}
        aria-label={`Edit ${what} "${text}"${keys.length === 1 ? ` (${keys[0]})` : ''}`}
        data-pencil-for={source.id}
        {...STOP_HANDLERS}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          onOpen(source.indexes);
        }}
        onFocus={() => onFocusChange(source.id)}
        onBlur={() => onFocusChange(null)}
      >
        <EditOutlinedIcon />
        {source.source !== 'text' && <AttributeBadge aria-hidden>{BADGE[source.source]}</AttributeBadge>}
      </Pencil>
    </Tooltip>
  );
};

interface Frame {
  readonly sources: ReadonlyArray<MeasuredSource>;
  readonly placements: ReadonlyMap<string, PencilPlacement>;
}

const EMPTY_FRAME: Frame = { sources: [], placements: new Map() };

interface Props {
  /** A pencil was activated: the marker indexes of its text. */
  readonly onOpen: (indexes: ReadonlyArray<number>) => void;
}

/**
 * Text edit mode affordances: a pencil button per visible marked text, drawn in one fixed layer above the app, so
 * disabled controls, menus, tooltips' attribute texts and grid headers are editable whatever their own state. Mounted
 * only in edit mode — nothing runs otherwise.
 */
const PencilOverlay = ({ onOpen }: Props) => {
  const [frame, setFrame] = useState<Frame>(EMPTY_FRAME);
  const [hovered, setHovered] = useState<string | null>(null);
  const [focused, setFocused] = useState<string | null>(null);
  const frameRef = useRef<Frame>(EMPTY_FRAME);
  // Last pointer position, and the pencil under it: a pencil sits half outside its text, so being on it keeps its text
  // hovered even where the geometry alone would not.
  const pointer = useRef<{ x: number; y: number; pencilFor: string | null } | null>(null);

  useEffect(() => {
    // Mutable scheduling state, local to this effect: one scan/measure per animation frame at most.
    const state = {
      raf: 0,
      rescan: true,
      sources: [] as ReturnType<typeof scanMarkedSources>,
    };

    const hoverOf = (p: { x: number; y: number; pencilFor: string | null }) =>
      p.pencilFor ??
      pipe(
        hoveredTarget(frameRef.current.sources, p.x, p.y),
        Option.map((s) => s.id),
        Option.getOrNull,
      );

    const run = () => {
      state.raf = 0;
      if (state.rescan) {
        state.sources = scanMarkedSources(document.body, isOwnUi);
        state.rescan = false;
      }
      const sources = measureSources(state.sources, browserMeasure(isOwnUi));
      const placements = layoutPencils(
        sources.map((s) => ({ id: s.id, rect: s.rect, slot: s.slot })),
        { ...DEFAULT_LAYOUT, viewport: { width: window.innerWidth, height: window.innerHeight } },
      );
      const next = { sources, placements: new Map(placements.map((p) => [p.id, p])) };
      frameRef.current = next;
      setFrame(next);
      if (pointer.current) setHovered(hoverOf(pointer.current));
    };

    const schedule = (rescan: boolean) => {
      state.rescan = state.rescan || rescan;
      if (state.raf === 0) state.raf = requestAnimationFrame(run);
    };

    const observer = new MutationObserver((records) => {
      const outside = records.filter(
        (r) => !isInsideEditorUi(r.target instanceof Element ? r.target : r.target.parentElement),
      );
      if (outside.length === 0) return;
      const changesText = outside.some(
        (r) => r.type !== 'attributes' || (r.attributeName !== null && TEXT_ATTRIBUTES.includes(r.attributeName)),
      );
      schedule(changesText);
    });
    observer.observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true });

    const onMove = (e: PointerEvent) => {
      const overPencil = e.target instanceof Element ? e.target.closest('[data-pencil-for]') : null;
      pointer.current = { x: e.clientX, y: e.clientY, pencilFor: overPencil?.getAttribute('data-pencil-for') ?? null };
      setHovered(hoverOf(pointer.current));
    };
    const onLeave = () => {
      pointer.current = null;
      setHovered(null);
    };
    const onScroll = () => schedule(false);
    const onResize = () => schedule(false);
    const interval = window.setInterval(() => schedule(false), REMEASURE_INTERVAL_MS);

    window.addEventListener('pointermove', onMove, { capture: true, passive: true });
    document.documentElement.addEventListener('pointerleave', onLeave);
    window.addEventListener('scroll', onScroll, { capture: true, passive: true });
    window.addEventListener('resize', onResize);
    schedule(true);

    return () => {
      observer.disconnect();
      window.clearInterval(interval);
      if (state.raf !== 0) cancelAnimationFrame(state.raf);
      window.removeEventListener('pointermove', onMove, { capture: true });
      document.documentElement.removeEventListener('pointerleave', onLeave);
      window.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const outlined = pipe(
    frame.sources,
    A.findFirst((s) => s.id === (hovered ?? focused)),
  );

  return createPortal(
    <Layer {...{ [EDITOR_UI_ATTRIBUTE]: '' }} data-testid="text-overrides-pencil-layer">
      {Option.match(outlined, {
        onNone: () => null,
        onSome: (s) => (
          <Outline
            style={{
              left: s.rect.left - 2,
              top: s.rect.top - 2,
              width: s.rect.right - s.rect.left + 4,
              height: s.rect.bottom - s.rect.top + 4,
            }}
          />
        ),
      })}
      {frame.sources.map((source) => {
        const placement = frame.placements.get(source.id);
        return placement ? (
          <PencilButton
            key={source.id}
            source={source}
            placement={placement}
            emphasised={source.id === hovered || source.id === focused}
            onOpen={onOpen}
            onFocusChange={setFocused}
          />
        ) : null;
      })}
    </Layer>,
    document.body,
  );
};

export default PencilOverlay;
